/**
 * Bunny Optimizer — client-side image URL optimization
 *
 * Converts ConvexFS redirect URLs or direct Bunny CDN URLs into
 * optimized URLs with resize/quality parameters.
 *
 * Requires:
 * - Bunny Optimizer enabled on the Pull Zone
 * - NEXT_PUBLIC_BUNNY_CDN_HOSTNAME env var set to the Pull Zone hostname
 */

const CDN_HOSTNAME = process.env.NEXT_PUBLIC_BUNNY_CDN_HOSTNAME;

/**
 * Converts any image URL to an optimized Bunny CDN URL.
 *
 * Handles:
 * - ConvexFS redirect URLs → extracts blobId → direct CDN URL + optimizer params
 * - Direct Bunny CDN URLs → appends optimizer params
 * - Other URLs (data:, blob:, non-CDN) → returned as-is
 */
export function optimizeBunnyUrl(
  url: string | undefined,
  opts: { width?: number; height?: number; quality?: number; format?: string } = {}
): string {
  if (!url || !CDN_HOSTNAME) return url || "";
  if (!url.startsWith("http")) return url;

  let targetUrl: string;

  // ConvexFS redirect URL → extract blobId
  const blobMatch = url.match(/\/fs\/blobs\/([^?/]+)/);
  if (blobMatch) {
    targetUrl = `https://${CDN_HOSTNAME}/${blobMatch[1]}`;
  } else if (url.includes(CDN_HOSTNAME)) {
    targetUrl = url;
  } else {
    return url;
  }

  // BARE direct CDN url — NO optimizer params. Source images are already
  // compressed; the Bunny Optimizer params (?quality&format&width) return 404
  // for the compressed blobs, while bare urls serve fine. (opts kept for
  // call-site compatibility.)
  void opts;
  return targetUrl;
}


/**
 * Gets a direct Bunny CDN URL (no optimizer params).
 * Useful for downloads where you want the full-quality original.
 */
export function toBunnyCdnUrl(url: string | undefined): string {
  if (!url || !CDN_HOSTNAME) return url || "";
  if (!url.startsWith("http")) return url;

  const blobMatch = url.match(/\/fs\/blobs\/([^?/]+)/);
  if (blobMatch) {
    return `https://${CDN_HOSTNAME}/${blobMatch[1]}`;
  }

  return url;
}

/**
 * Pick a product's best display image and resolve it to a URL that actually
 * loads in a plain <img> AND can be fetched by the AI flyer/promotion
 * generators. The raw product fields from getProductsByBrand are UNRESOLVED:
 * many brands store `imageUrl` as the ConvexFS `/fs/blobs/<id>` proxy form,
 * which 302-redirects per request and fails / rate-limits on a plain <img>
 * (and on the generator's server-side fetch). toBunnyCdnUrl rewrites that to the
 * stable bare CDN URL; bare-CDN and /api/storage URLs pass through unchanged.
 * Used by the shared StudioProductPicker + the flyer/promotion generators so the
 * picker thumbnails AND the product images sent to the AI both resolve.
 */
export function resolveProductImageUrl(p: any): string {
  const raw =
    p?.primaryImage ||
    p?.imageUrl ||
    p?.images?.[0] ||
    p?.structuredImages?.front?.url ||
    "";
  return raw ? toBunnyCdnUrl(raw) : "";
}
